import { html, raw } from 'onefold';
import { CodeBlock } from '../../components/CodeBlock';

export function ApiReferencePage(): Node {
  return html`
    <div>
      <h1>Microfrontend API Reference</h1>
      <p>Complete reference for <code>loadRemote</code> and <code>configureSecurity</code>.</p>

      <h2>loadRemote(options)</h2>
      ${raw(`<table>
        <tr><th>Option</th><th>Type</th><th>Required</th><th>Default</th><th>Description</th></tr>
        <tr><td><code>url</code></td><td>string</td><td>Yes</td><td>—</td><td>URL of the remote ES module.</td></tr>
        <tr><td><code>integrity</code></td><td>string</td><td>No*</td><td>—</td><td>SRI hash (sha256/sha384/sha512).</td></tr>
        <tr><td><code>fallback</code></td><td>() => Node</td><td>No</td><td>—</td><td>UI to show while loading.</td></tr>
        <tr><td><code>onError</code></td><td>(err: Error) => Node</td><td>No</td><td>—</td><td>UI to show on failure.</td></tr>
        <tr><td><code>props</code></td><td>Record&lt;string, any&gt;</td><td>No</td><td>{}</td><td>Props passed to the remote default export.</td></tr>
        <tr><td><code>isolation</code></td><td>'none' | 'shadow' | 'iframe'</td><td>No</td><td>'none'</td><td>DOM isolation mode.</td></tr>
        <tr><td><code>timeout</code></td><td>number</td><td>No</td><td>10000</td><td>Max load time in ms (overrides global).</td></tr>
      </table>`)}
      <p>* Required when <code>configureSecurity({ requireIntegrity: true })</code> is active.</p>

      <h2>configureSecurity(options)</h2>
      <table>
        <tr><th>Option</th><th>Type</th><th>Required</th><th>Default</th><th>Description</th></tr>
        <tr><td><code>trustedOrigins</code></td><td>string[]</td><td>No</td><td>[]</td><td>Allowed origins. Modules from other origins are rejected.</td></tr>
        <tr><td><code>requireIntegrity</code></td><td>boolean</td><td>No</td><td>false</td><td>Require SRI hash for all loadRemote calls.</td></tr>
        <tr><td><code>blockAll</code></td><td>boolean</td><td>No</td><td>false</td><td>Kill switch — blocks all remote loading.</td></tr>
        <tr><td><code>timeout</code></td><td>number</td><td>No</td><td>10000</td><td>Global timeout in ms for remote loads.</td></tr>
      </table>

      <h2>embedForeign(options)</h2>
      ${raw(`<table>
        <tr><th>Option</th><th>Type</th><th>Required</th><th>Description</th></tr>
        <tr><td><code>mount</code></td><td>(container, props) => context</td><td>Yes</td><td>Mount the foreign framework into the container element.</td></tr>
        <tr><td><code>unmount</code></td><td>(container, context) => void</td><td>Yes</td><td>Cleanup when the node is removed from DOM.</td></tr>
        <tr><td><code>props</code></td><td>Record&lt;string, any&gt;</td><td>No</td><td>Props passed to the mount function.</td></tr>
      </table>`)}

      <h2>Return Values</h2>
      ${CodeBlock(`// loadRemote returns a Node (renders immediately with fallback)
const node: Node = loadRemote({ url: '...', fallback: () => html\`...\` });

// configureSecurity returns void (global side effect)
configureSecurity({ trustedOrigins: ['...'] });

// embedForeign returns a Node
const node: Node = embedForeign({ mount: ..., unmount: ... });`)}
    </div>
  `;
}
